/**
 * In-memory credential store for renter-provided credentials.
 *
 * Security design:
 * - Never persisted to disk (agentvault.json or any file)
 * - Scoped by room ID (each rental = one room)
 * - Auto-purges on process exit (SIGINT, SIGTERM, exit)
 * - Credential values stripped before any logging
 * - Nonce tracking for replay prevention
 */

export interface RenterCredential {
  key: string;
  value: string;           // plaintext — in-memory ONLY
  type: string;            // "api_key", "oauth_token", etc.
  scope: string;           // "read", "write", "admin"
  grantedAt: string;       // ISO 8601
  agreementId: string;
  roomId: string;
}

/** Redacted view of a credential (for logging / context without values). */
export interface CredentialInfo {
  key: string;
  type: string;
  scope: string;
  grantedAt: string;
}

export class CredentialStore {
  /** Map<roomId, Map<credentialKey, RenterCredential>> */
  private _store = new Map<string, Map<string, RenterCredential>>();
  /** Seen nonces for replay prevention — bounded per room */
  private _seenNonces = new Map<string, Set<string>>();
  private static readonly MAX_NONCES_PER_ROOM = 1000;

  constructor() {
    // Auto-purge on process exit
    const purge = () => this.purgeAll();
    process.on("exit", purge);
    process.on("SIGINT", () => { purge(); process.exit(0); });
    process.on("SIGTERM", () => { purge(); process.exit(0); });
  }

  /**
   * Check if a nonce has been seen (replay prevention).
   * Returns true if the nonce is new (not a replay), false if seen before.
   */
  checkNonce(roomId: string, nonce: string): boolean {
    if (!nonce) return true; // no nonce = no replay check

    let nonces = this._seenNonces.get(roomId);
    if (!nonces) {
      nonces = new Set();
      this._seenNonces.set(roomId, nonces);
    }

    if (nonces.has(nonce)) return false; // replay!

    nonces.add(nonce);
    // Bound the set size
    if (nonces.size > CredentialStore.MAX_NONCES_PER_ROOM) {
      const iter = nonces.values();
      // Remove oldest entries (first 100)
      for (let i = 0; i < 100; i++) {
        const val = iter.next().value;
        if (val !== undefined) nonces.delete(val);
      }
    }
    return true;
  }

  /** Store a credential for a room. */
  grant(roomId: string, credential: RenterCredential): void {
    let room = this._store.get(roomId);
    if (!room) {
      room = new Map();
      this._store.set(roomId, room);
    }
    room.set(credential.key, credential);
  }

  /** Revoke a specific credential. */
  revoke(roomId: string, key: string): boolean {
    const room = this._store.get(roomId);
    if (!room) return false;
    return room.delete(key);
  }

  /** Revoke all credentials for a room. */
  revokeAll(roomId: string): void {
    this._store.delete(roomId);
  }

  /** Get a credential value. */
  get(roomId: string, key: string): RenterCredential | undefined {
    return this._store.get(roomId)?.get(key);
  }

  /** Get all credentials for a room (values included — only for agent context injection). */
  getAll(roomId: string): RenterCredential[] {
    const room = this._store.get(roomId);
    if (!room) return [];
    return Array.from(room.values());
  }

  /** Get credential info without values (safe for logging). */
  getInfo(roomId: string): CredentialInfo[] {
    const room = this._store.get(roomId);
    if (!room) return [];
    return Array.from(room.values()).map((c) => ({
      key: c.key,
      type: c.type,
      scope: c.scope,
      grantedAt: c.grantedAt,
    }));
  }

  /** Check if a specific credential exists. */
  has(roomId: string, key: string): boolean {
    return this._store.get(roomId)?.has(key) ?? false;
  }

  /** Get credential count for a room. */
  count(roomId: string): number {
    return this._store.get(roomId)?.size ?? 0;
  }

  /** Purge all credentials for a room (rental end). */
  purgeForRoom(roomId: string): void {
    this._store.delete(roomId);
    this._seenNonces.delete(roomId);
  }

  /** Purge everything (process exit). */
  purgeAll(): void {
    this._store.clear();
    this._seenNonces.clear();
  }

  /** Get a map of credential key → value for context injection. */
  getCredentialMap(roomId: string): Record<string, string> {
    const room = this._store.get(roomId);
    if (!room) return {};
    const result: Record<string, string> = {};
    for (const [key, cred] of room) {
      result[key] = cred.value;
    }
    return result;
  }
}
